import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { ComponentType, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateFaq, Faq, isOfTypeFaq } from '../model/faq.types';

import rehypeSanitize from 'rehype-sanitize';
import { lazy, Suspense } from 'react';
import { MDEditorProps } from '@uiw/react-md-editor';

import { Select } from '@starjack/shared-components';
import { LoadingButton } from '@mui/lab';
import { DrawerActions } from '@starjack/shared-components';
import { useNotification } from '@starjack/features/notifications';
import {
    useGetFaqsQuery,
    usePostFaqMutation,
    usePutFaqMutation,
    useDeleteFaqMutation,
} from '@starjack/entities/faq';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';

const MDEditor: ComponentType<MDEditorProps> = lazy(() =>
    import('@uiw/react-md-editor').then((mod) => ({ default: mod.default }))
);

export const pagesMenuItems = [
    {
        value: '/products/:id',
        label: 'Produktseite',
    },
    {
        value: '/my-data',
        label: 'Mein Konto',
    },
    {
        value: '/cart',
        label: 'Checkout',
    },
];

export type FaqDrawerContentProps = {
    faq: Faq | CreateFaq;
    onClose: () => void;
    canMutateFaq?: (faq: Faq) => boolean;
};

export const FaqDrawerContent: FunctionComponent<FaqDrawerContentProps> = ({
    faq,
    onClose,
    canMutateFaq,
}) => {
    const [oldPropsFaq, setOldPropsFaq] = useState<Faq | CreateFaq>(faq);
    const [localFaq, setLocalFaq] = useState(faq);
    const [currentTab, setCurrentTab] = useState<'de' | 'en'>('de');
    const { t } = useTranslation();
    const { addNotification } = useNotification();

    const { data = [] } = useGetFaqsQuery({ filtered: true });
    const [postFaq, { isLoading: postLoading }] = usePostFaqMutation();
    const [putFaq, { isLoading: putLoading }] = usePutFaqMutation();
    const [deleteFaq, { isLoading: deleteLoading }] = useDeleteFaqMutation();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    if (oldPropsFaq['id'] !== localFaq['id']) {
        setOldPropsFaq(faq);
        setLocalFaq(faq);
    }

    const onCreateOrUpdate = () => {
        const mutationHandler = isOfTypeFaq(localFaq) ? putFaq : postFaq;

        mutationHandler(localFaq)
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'FAQ erfolgreich gespeichert',
                    type: 'success',
                });

                onClose();
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    return (
        <Box
            sx={{
                width: '800px',
            }}
        >
            <Box
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.dark,
                    color: '#fff',
                    p: 4,
                })}
            >
                <Typography variant='h6'>
                    {isOfTypeFaq(localFaq)
                        ? 'FAQ bearbeiten'
                        : 'Neue FAQ erstellen'}
                </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 4, pt: 2 }}>
                <Tabs
                    value={currentTab}
                    onChange={(_, val) => setCurrentTab(val)}
                >
                    <Tab label='Deutsch (DE)' value='de' />
                    <Tab label='English (EN)' value='en' />
                </Tabs>
            </Box>

            <Grid2
                sx={{
                    p: 4,
                }}
                spacing={4}
                container
            >
                {currentTab === 'de' && (
                    <>
                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <TextField
                                sx={{
                                    width: '100%',
                                }}
                                label='Frage (DE) *'
                                value={localFaq.titleMulti.de}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setLocalFaq((prevState) => ({
                                        ...prevState,
                                        titleMulti: {
                                            ...prevState.titleMulti,
                                            de: value,
                                        },
                                    }));
                                }}
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <Typography gutterBottom>
                                Antwort (DE) *:
                            </Typography>
                            <Suspense fallback={<div>Loading editor...</div>}>
                                <MDEditor
                                    className={'wmde-markdown'}
                                    value={localFaq.textMulti.de}
                                    onChange={(value) => {
                                        setLocalFaq((prevState) => ({
                                            ...prevState,
                                            textMulti: {
                                                ...prevState.textMulti,
                                                de: value || '',
                                            },
                                        }));
                                    }}
                                    previewOptions={{
                                        rehypePlugins: [[rehypeSanitize]],
                                    }}
                                    visibleDragbar={false}
                                />
                            </Suspense>
                        </Grid2>
                    </>
                )}

                {currentTab === 'en' && (
                    <>
                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <TextField
                                sx={{
                                    width: '100%',
                                }}
                                label='Question (EN) *'
                                value={localFaq.titleMulti.en}
                                onChange={(e) => {
                                    const { value } = e.target;
                                    setLocalFaq((prevState) => ({
                                        ...prevState,
                                        titleMulti: {
                                            ...prevState.titleMulti,
                                            en: value,
                                        },
                                    }));
                                }}
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <Typography gutterBottom>Answer (EN) *:</Typography>
                            <Suspense fallback={<div>Loading editor...</div>}>
                                <MDEditor
                                    className={'wmde-markdown'}
                                    value={localFaq.textMulti.en}
                                    onChange={(value) => {
                                        setLocalFaq((prevState) => ({
                                            ...prevState,
                                            textMulti: {
                                                ...prevState.textMulti,
                                                en: value || '',
                                            },
                                        }));
                                    }}
                                    previewOptions={{
                                        rehypePlugins: [[rehypeSanitize]],
                                    }}
                                    visibleDragbar={false}
                                />
                            </Suspense>
                        </Grid2>
                    </>
                )}

                <Grid2
                    size={{
                        md: 6,
                    }}
                >
                    <Select
                        label='Themenbereich auswählen'
                        InputLabelProps={{ shrink: true }}
                        value={localFaq.groupId || 'no-group'}
                        onChange={(e) => {
                            const { value } = e.target;

                            const copyFaq = { ...localFaq };
                            if (value === 'no-group') {
                                copyFaq.groupId = null;
                            } else {
                                copyFaq.groupId = data.find(
                                    (g) => g.id === value
                                ).id;
                            }

                            setLocalFaq(copyFaq);
                        }}
                    >
                        {data.map((group, index) => (
                            <option key={index} value={group.id}>
                                {group.name.de}
                            </option>
                        ))}
                    </Select>
                </Grid2>

                <Grid2
                    size={{
                        md: 6,
                    }}
                >
                    <TextField
                        sx={{
                            width: '100%',
                        }}
                        type='number'
                        label='Priorität'
                        value={localFaq.priority}
                        onChange={(e) => {
                            const { value } = e.target;
                            const valNumber = parseInt(value);
                            const next = valNumber < 0 ? 0 : valNumber;

                            setLocalFaq((prevState) => ({
                                ...prevState,
                                priority: next,
                            }));
                        }}
                    />
                </Grid2>

                <Grid2
                    size={{
                        md: 6,
                    }}
                >
                    <Select
                        sx={{
                            width: '360px',
                        }}
                        value={localFaq.page || 'no_page'}
                        label='Ausgabeseite auswählen'
                        // onChange={handlePageSelect}
                    >
                        <option value={'no_page'}>Nur im Menü FAQ</option>
                        {pagesMenuItems.map(({ value, label }) => (
                            <option key={value} value={value}>
                                {label}
                            </option>
                        ))}
                    </Select>
                </Grid2>
            </Grid2>

            <DrawerActions>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Box>
                        {isOfTypeFaq(faq) &&
                            (!canMutateFaq || canMutateFaq(faq)) && (
                                <Button
                                    onClick={() => setShowDeleteConfirm(true)}
                                    color='error'
                                    variant='outlined'
                                    startIcon={
                                        <SvgIcon fontSize='small'>
                                            <TrashIcon />
                                        </SvgIcon>
                                    }
                                >
                                    {t('faq.deleteButton')}
                                </Button>
                            )}
                    </Box>
                    <Stack
                        sx={{
                            alignItems: 'center',
                        }}
                        spacing={2}
                        direction='row'
                    >
                        <Button onClick={onClose} variant='outlined'>
                            {t('faq.cancelButton')}
                        </Button>
                        <LoadingButton
                            onClick={onCreateOrUpdate}
                            variant='contained'
                            loading={postLoading || putLoading}
                            disabled={
                                !(
                                    (localFaq.titleMulti.de ||
                                        localFaq.titleMulti.en) &&
                                    (localFaq.textMulti.de ||
                                        localFaq.textMulti.en)
                                )
                            }
                        >
                            Speichern
                        </LoadingButton>
                    </Stack>
                </Box>
            </DrawerActions>

            <Dialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
            >
                <DialogTitle>{t('faq.deleteDialogTitle')}</DialogTitle>
                <DialogContent>{t('faq.deleteDialogContent')}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setShowDeleteConfirm(false)}
                        variant='outlined'
                    >
                        {t('faq.cancelButton')}
                    </Button>
                    <LoadingButton
                        loading={deleteLoading}
                        onClick={() => {
                            if (!isOfTypeFaq(localFaq)) return;
                            deleteFaq({ faqId: localFaq.id })
                                .unwrap()
                                .then(() => {
                                    addNotification({
                                        type: 'success',
                                        message: t('faq.deleteSuccess'),
                                    });
                                    setShowDeleteConfirm(false);
                                    onClose();
                                })
                                .catch(() => {
                                    addNotification({
                                        type: 'error',
                                        message: t('faq.deleteError'),
                                    });
                                });
                        }}
                        color='error'
                        variant='contained'
                    >
                        {t('faq.deleteButton')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
